import { useEffect, useState } from "react";
import api, { fmtIDR, fmtDate, BACKEND } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, Trash2, Settings2, Calculator } from "lucide-react";

const periodNow = () => new Date().toISOString().slice(0, 7);

export default function Payroll() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState({ staff_id: "", period: periodNow(), bonus: 0, base_salary: "", overtime_pay: "", late_deduction: "", kasbon_deduction: "" });
  const [rules, setRules] = useState({ overtime_rate_per_hour: 20000, late_penalty_per_minute: 1000 });
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/payroll").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/settings/payroll").then(({ data }) => setRules(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!gen.staff_id) return toast.error("Pilih karyawan");
    setSaving(true);
    const num = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));
    const payload = { staff_id: gen.staff_id, period: gen.period, bonus: Number(gen.bonus || 0) };
    ["base_salary", "overtime_pay", "late_deduction", "kasbon_deduction"].forEach((k) => {
      const n = num(gen[k]); if (n !== undefined) payload[k] = n;
    });
    try {
      await api.post("/payroll/generate", payload);
      toast.success("Slip gaji dibuat"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setSaving(false); }
  };

  const saveRules = async () => {
    try {
      await api.post("/settings/payroll", { overtime_rate_per_hour: Number(rules.overtime_rate_per_hour), late_penalty_per_minute: Number(rules.late_penalty_per_minute) });
      toast.success("Pengaturan disimpan");
    } catch { toast.error("Gagal"); }
  };

  const downloadSlip = (r) => {
    const url = `${BACKEND}/api/payroll/${r.id}/slip`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `slip-gaji-${r.staff_name}-${r.period}.pdf`;
        a.click();
      })
      .catch(() => toast.error("Gagal mengunduh slip"));
  };

  const del = async (r) => { if (!window.confirm("Hapus slip?")) return; await api.delete(`/payroll/${r.id}`); load(); };

  return (
    <div>
      <PageHeader title="Penggajian & Slip Gaji" subtitle="Kalkulasi gaji otomatis: pokok + lembur + bonus - telat - kasbon" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3"><Calculator className="w-4 h-4 text-sky-600" /><h3 className="font-medium text-slate-800">Generate Slip Gaji</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Karyawan</Label>
              <Select value={gen.staff_id} onValueChange={(v) => setGen({ ...gen, staff_id: v })}>
                <SelectTrigger className="mt-1" data-testid="payroll-staff"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Periode</Label><Input type="month" value={gen.period} onChange={(e) => setGen({ ...gen, period: e.target.value })} className="mt-1" data-testid="payroll-period" /></div>
            <div><Label className="text-xs text-slate-500">Bonus</Label><Input type="number" value={gen.bonus} onChange={(e) => setGen({ ...gen, bonus: e.target.value })} className="mt-1" data-testid="payroll-bonus" /></div>
          </div>
          <Button onClick={generate} disabled={saving} className="mt-3 bg-sky-600 hover:bg-sky-700" data-testid="payroll-generate-btn">{saving ? "Memproses..." : "Generate Slip"}</Button>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-2">Input manual (opsional) — kosongkan untuk hitung otomatis dari absensi & kasbon.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-500">Gaji Pokok</Label><Input type="number" placeholder="otomatis" value={gen.base_salary} onChange={(e) => setGen({ ...gen, base_salary: e.target.value })} className="mt-1" data-testid="payroll-base" /></div>
              <div><Label className="text-xs text-slate-500">Bonus Lembur</Label><Input type="number" placeholder="otomatis" value={gen.overtime_pay} onChange={(e) => setGen({ ...gen, overtime_pay: e.target.value })} className="mt-1" data-testid="payroll-overtime" /></div>
              <div><Label className="text-xs text-slate-500">Potongan Telat</Label><Input type="number" placeholder="otomatis" value={gen.late_deduction} onChange={(e) => setGen({ ...gen, late_deduction: e.target.value })} className="mt-1" data-testid="payroll-late" /></div>
              <div><Label className="text-xs text-slate-500">Potongan Kasbon</Label><Input type="number" placeholder="otomatis" value={gen.kasbon_deduction} onChange={(e) => setGen({ ...gen, kasbon_deduction: e.target.value })} className="mt-1" data-testid="payroll-kasbon" /></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3"><Settings2 className="w-4 h-4 text-slate-500" /><h3 className="font-medium text-slate-800">Aturan Gaji</h3></div>
          <div className="space-y-2">
            <div><Label className="text-xs text-slate-500">Tarif Lembur / jam</Label><Input type="number" value={rules.overtime_rate_per_hour} onChange={(e) => setRules({ ...rules, overtime_rate_per_hour: e.target.value })} className="mt-1" data-testid="rule-overtime" /></div>
            <div><Label className="text-xs text-slate-500">Potongan Telat / menit</Label><Input type="number" value={rules.late_penalty_per_minute} onChange={(e) => setRules({ ...rules, late_penalty_per_minute: e.target.value })} className="mt-1" data-testid="rule-late" /></div>
            <Button onClick={saveRules} variant="outline" className="w-full mt-1" data-testid="rule-save">Simpan Aturan</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Periode", "Karyawan", "Pokok", "Lembur", "Bonus", "Pot. Telat", "Pot. Kasbon", "Gaji Bersih", "Slip"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={9}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={9}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`payroll-row-${r.id}`}>
                  <td className="px-4 py-3">{r.period}</td>
                  <td className="px-4 py-3">{r.staff_name}</td>
                  <td className="px-4 py-3 font-mono-num">{fmtIDR(r.base_salary)}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{fmtIDR(r.overtime_pay)}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{fmtIDR(r.bonus)}</td>
                  <td className="px-4 py-3 font-mono-num text-red-600">{fmtIDR(r.late_deduction)}</td>
                  <td className="px-4 py-3 font-mono-num text-red-600">{fmtIDR(r.kasbon_deduction)}</td>
                  <td className="px-4 py-3 font-mono-num font-semibold text-slate-900">{fmtIDR(r.net_salary)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="text-xs mr-1" onClick={() => downloadSlip(r)} data-testid={`slip-${r.id}`}><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                    <button onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
